import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heart, Eye, BookOpen, Star, Download, Play, Clock, User, ChevronRight, Share2, Lock, Sparkles, Crown, ShoppingCart } from 'lucide-react';
import { getComic, getEpisodes, likeComic, favoriteComic, getComicPdfUrl, getComicBundleInfo, checkComicEpisodesAccess, createChapterCheckout } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AdBanner } from '../components/AdBanner';
import { TipButton } from '../components/TipButton';
import { PremiumBadge } from '../components/LockedEpisode';
import { ShareButton } from '../components/ShareButton';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const ComicDetailPage = () => {
  const { comicId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [comic, setComic] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesAccess, setEpisodesAccess] = useState({});
  const [bundleInfo, setBundleInfo] = useState(null);
  const [liked, setLiked] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasingEpisode, setPurchasingEpisode] = useState(null);
  
  const FREE_EPISODE_LIMIT = 2; // First 2 episodes are free
  const DEFAULT_EPISODE_PRICE = 1.50;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [comicRes, episodesRes, bundleRes] = await Promise.all([
          getComic(comicId),
          getEpisodes(comicId),
          getComicBundleInfo(comicId).catch(() => ({ data: null }))
        ]);
        setComic(comicRes.data);
        setEpisodes(episodesRes.data);
        setBundleInfo(bundleRes.data);
        setLiked(comicRes.data.is_liked || false);
        setFavorited(comicRes.data.is_favorited || false);
        
        // Check episode access if authenticated
        if (isAuthenticated) {
          try {
            const accessRes = await checkComicEpisodesAccess(comicId);
            const accessMap = {};
            accessRes.data.episodes?.forEach(ep => {
              accessMap[ep.episode_id] = ep;
            });
            setEpisodesAccess(accessMap);
          } catch (err) {
            console.error('Failed to check episode access:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching comic:', error);
        toast.error('Failed to load comic');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [comicId, isAuthenticated]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      const res = await likeComic(comicId);
      setLiked(res.data.liked);
      setComic(prev => ({
        ...prev,
        likes: prev.likes + (res.data.liked ? 1 : -1)
      }));
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      const res = await favoriteComic(comicId);
      setFavorited(res.data.favorited);
      toast.success(res.data.favorited ? 'Added to library' : 'Removed from library');
    } catch (error) {
      toast.error('Failed to update library');
    }
  };

  const handlePurchaseEpisode = async (episode, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    
    setPurchasingEpisode(episode.id);
    try {
      const res = await createChapterCheckout(episode.id, window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasingEpisode(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
          <p className="text-[var(--text-secondary)]">Loading comic...</p>
        </div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--background)]">
        <div className="text-center">
          <h1 className="text-3xl font-teko mb-4">Comic not found</h1>
          <p className="text-[var(--text-secondary)] mb-6">This comic may have been removed or does not exist.</p>
          <Link to="/browse" className="btn-hotrod">
            <span>Browse Comics</span>
          </Link>
        </div>
      </div>
    );
  }

  const firstEpisode = episodes[0];

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="comic-detail-page">
      <SEO 
        title={comic.title}
        description={comic.description}
        image={comic.cover_image}
        url={`/comic/${comicId}`}
        type="article"
        author={comic.creator_name || comic.artist_name}
      />
      
      {/* Hero Section - Cinematic Banner */}
      <div className="relative h-[60vh] min-h-[500px] overflow-hidden">
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ 
            backgroundImage: `url(${comic.cover_image})`,
            filter: 'blur(20px)'
          }}
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--background)]/80 via-transparent to-[var(--background)]/80" />
        
        {/* Content */}
        <div className="absolute inset-0 flex items-end">
          <div className="page-padding pb-12 w-full">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-8 items-end">
              
              {/* Cover Image */}
              <div className="w-48 md:w-56 lg:w-64 flex-shrink-0 mx-auto md:mx-0">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity" />
                  <img 
                    src={comic.cover_image} 
                    alt={comic.title}
                    className="relative w-full aspect-[2/3] object-cover rounded-xl shadow-2xl"
                    data-testid="comic-cover"
                  />
                  {!comic.is_free && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500/90 backdrop-blur-sm rounded-full text-xs font-medium text-white flex items-center gap-1">
                      <Sparkles size={10} />
                      Premium
                    </div>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                {/* Genre Tag */}
                <span className="inline-block px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs font-medium uppercase tracking-wider rounded-full mb-4">
                  {comic.genre?.replace('_', ' ')}
                </span>
                
                {/* Title */}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-teko font-semibold leading-tight mb-3" data-testid="comic-title">
                  {comic.title}
                </h1>
                
                {/* Creator */}
                <Link 
                  to={`/creator/${comic.creator_id || comic.artist_id}`}
                  className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors mb-4"
                  data-testid="comic-creator"
                >
                  <User size={16} />
                  <span>by <span className="text-[var(--foreground)]">{comic.creator_name || comic.artist_name}</span></span>
                </Link>

                {/* Stats Row */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 mb-6">
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Eye size={18} />
                    <span className="font-medium text-[var(--foreground)]">{(comic.views || 0).toLocaleString()}</span>
                    <span className="text-sm">views</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Heart size={18} className={liked ? 'text-[var(--secondary)] fill-[var(--secondary)]' : ''} />
                    <span className="font-medium text-[var(--foreground)]">{(comic.likes || 0).toLocaleString()}</span>
                    <span className="text-sm">likes</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <BookOpen size={18} />
                    <span className="font-medium text-[var(--foreground)]">{comic.episode_count || 0}</span>
                    <span className="text-sm">episodes</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  {firstEpisode && (
                    <Link 
                      to={`/read/${comicId}/${firstEpisode.id}`}
                      className="flex items-center gap-2 px-8 py-3 bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-full font-medium transition-all hover:scale-105"
                      data-testid="start-reading-btn"
                    >
                      <Play size={18} fill="currentColor" />
                      Start Reading
                    </Link>
                  )}
                  
                  <button
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                      liked 
                        ? 'bg-[var(--secondary)]/20 text-[var(--secondary)] border border-[var(--secondary)]' 
                        : 'bg-[var(--surface)] text-[var(--foreground)] border border-[var(--border)] hover:border-[var(--secondary)]'
                    }`}
                    data-testid="like-btn"
                  >
                    <Heart size={18} fill={liked ? 'currentColor' : 'none'} />
                    {liked ? 'Liked' : 'Like'}
                  </button>
                  
                  <button
                    onClick={handleFavorite}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                      favorited 
                        ? 'bg-amber-500/20 text-amber-500 border border-amber-500' 
                        : 'bg-[var(--surface)] text-[var(--foreground)] border border-[var(--border)] hover:border-amber-500'
                    }`}
                    data-testid="favorite-btn"
                  >
                    <Star size={18} fill={favorited ? 'currentColor' : 'none'} />
                    {favorited ? 'In Library' : 'Add to Library'}
                  </button>
                  
                  <ShareButton 
                    title={comic.title}
                    description={comic.description}
                    image={comic.cover_image}
                    variant="icon"
                    className="!p-3 rounded-full bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-padding py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Left Column - Description & Episodes */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* About Section */}
              <section className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6">
                <h2 className="text-xl font-teko mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[var(--primary)] rounded-full" />
                  About this Comic
                </h2>
                <p className="text-[var(--text-secondary)] leading-relaxed" data-testid="comic-description">
                  {comic.description}
                </p>
                
                {/* Tags */}
                {comic.tags && comic.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[var(--border)]">
                    {comic.tags.map((tag) => (
                      <span key={`tag-${tag}`} className="px-3 py-1 bg-[var(--background)] text-[var(--text-secondary)] text-xs rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </section>

              {/* Episodes Section */}
              <section data-testid="episodes-section">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-teko flex items-center gap-2">
                    <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                    Episodes
                    <span className="text-[var(--text-secondary)] text-lg">({episodes.length})</span>
                  </h2>
                  
                  {bundleInfo?.has_premium_content && bundleInfo.premium_episode_count > 1 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
                      <Sparkles size={14} className="text-green-500" />
                      <span className="text-sm text-green-500">
                        Bundle: ${bundleInfo.bundle_price?.toFixed(2)} (save {bundleInfo.discount_percentage}%)
                      </span>
                    </div>
                  )}
                </div>
                
                {episodes.length === 0 ? (
                  <div className="text-center py-12 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
                    <BookOpen size={40} className="mx-auto mb-4 text-[var(--text-secondary)]" />
                    <p className="text-[var(--text-secondary)]">No episodes available yet.</p>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">Check back soon!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {episodes.map((episode, index) => {
                      const episodeNumber = episode.episode_number || (index + 1);
                      const accessInfo = episodesAccess[episode.id];
                      const isLocked = episodeNumber > FREE_EPISODE_LIMIT && 
                        (!isAuthenticated || (user?.subscription_tier === 'free' && !accessInfo?.can_access));
                      const isPremiumUser = user?.subscription_tier && ['premium', 'creator', 'contributor'].includes(user.subscription_tier);
                      
                      return (
                      <React.Fragment key={episode.id}>
                        <Link
                          to={isLocked && !isPremiumUser ? '#' : `/read/${comicId}/${episode.id}`}
                          onClick={(e) => {
                            if (isLocked && !isPremiumUser) {
                              e.preventDefault();
                              if (!isAuthenticated) {
                                toast.error('Please login to continue reading');
                                navigate('/login');
                              } else {
                                toast.info('Upgrade to Premium for unlimited access!');
                              }
                            }
                          }}
                          className={`group flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl transition-all ${
                            isLocked && !isPremiumUser 
                              ? 'opacity-75 hover:border-amber-500/50' 
                              : 'hover:border-[var(--primary)]/50 hover:bg-[var(--surface-highlight)]'
                          }`}
                          data-testid={`episode-${episode.id}`}
                        >
                          {/* Episode Number */}
                          <div className={`w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-lg border transition-colors ${
                            isLocked && !isPremiumUser
                              ? 'bg-amber-500/10 border-amber-500/30'
                              : 'bg-[var(--background)] border-[var(--border)] group-hover:border-[var(--primary)]/30'
                          }`}>
                            {isLocked && !isPremiumUser ? (
                              <Lock size={20} className="text-amber-500" />
                            ) : (
                              <span className="text-2xl font-teko text-[var(--primary)]">
                                {String(episodeNumber).padStart(2, '0')}
                              </span>
                            )}
                          </div>
                          
                          {/* Episode Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-medium truncate transition-colors ${
                                isLocked && !isPremiumUser ? 'text-[var(--text-secondary)]' : 'group-hover:text-[var(--primary)]'
                              }`}>
                                {episode.title}
                              </h3>
                              {isLocked && !isPremiumUser && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full">
                                  <Crown size={10} />
                                  Premium
                                </span>
                              )}
                              {episode.is_premium && !isLocked && <PremiumBadge price={episode.price} />}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-secondary)]">
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {new Date(episode.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye size={12} />
                                {(episode.views || 0).toLocaleString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart size={12} />
                                {(episode.likes || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          
                          {/* Arrow, Lock, or Buy Button */}
                          {isLocked && !isPremiumUser ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => handlePurchaseEpisode(episode, e)}
                                disabled={purchasingEpisode === episode.id}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                                data-testid={`buy-episode-${episode.id}`}
                              >
                                {purchasingEpisode === episode.id ? (
                                  <>
                                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                                    <span>...</span>
                                  </>
                                ) : (
                                  <>
                                    <ShoppingCart size={14} />
                                    <span>${(episode.price || DEFAULT_EPISODE_PRICE).toFixed(2)}</span>
                                  </>
                                )}
                              </button>
                              <Lock size={18} className="text-amber-500" />
                            </div>
                          ) : (
                            <ChevronRight size={20} className="text-[var(--text-secondary)] group-hover:text-[var(--primary)] group-hover:translate-x-1 transition-all" />
                          )}
                        </Link>
                        
                        {/* Ad after 3rd episode */}
                        {index === 2 && episodes.length > 4 && (
                          <div className="py-2">
                            <AdBanner 
                              size="inFeed" 
                              variant="subtle" 
                              className="rounded-xl" 
                              comicId={comicId}
                            />
                          </div>
                        )}
                      </React.Fragment>
                      );
                    })}
                    
                    {/* Premium Upgrade Banner - show if there are locked episodes */}
                    {episodes.length > FREE_EPISODE_LIMIT && (!isAuthenticated || user?.subscription_tier === 'free') && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/30 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-full">
                            <Crown size={24} className="text-amber-500" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-amber-500">Unlock All Episodes</h4>
                            <p className="text-sm text-[var(--text-secondary)]">
                              Get Premium to read all episodes, or purchase individually
                            </p>
                          </div>
                          <Link 
                            to="/pricing" 
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
                          >
                            Upgrade
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              
              {/* Quick Actions Card */}
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 sticky top-24">
                <h3 className="text-lg font-teko mb-4">Quick Actions</h3>
                
                <div className="space-y-3">
                  {/* Download PDF */}
                  <a
                    href={getComicPdfUrl(comicId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 w-full p-3 bg-[var(--background)] hover:bg-[var(--primary)]/10 border border-[var(--border)] hover:border-[var(--primary)]/30 rounded-xl transition-all"
                    data-testid="download-pdf-btn"
                    onClick={() => toast.success('PDF download started!')}
                  >
                    <Download size={18} className="text-[var(--primary)]" />
                    <span>Download PDF</span>
                  </a>
                  
                  {/* Tip Creator */}
                  {comic.creator_id && (
                    <div className="w-full">
                      <TipButton 
                        creatorId={comic.creator_id}
                        creatorName={comic.creator_name || comic.artist_name}
                        comicId={comicId}
                        variant="default"
                      />
                    </div>
                  )}
                </div>
                
                {/* Creator Info */}
                <div className="mt-6 pt-6 border-t border-[var(--border)]">
                  <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-3">Created by</p>
                  <Link 
                    to={`/creator/${comic.creator_id || comic.artist_id}`}
                    className="flex items-center gap-3 p-3 bg-[var(--background)] hover:bg-[var(--surface-highlight)] border border-[var(--border)] rounded-xl transition-all"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white font-bold">
                      {(comic.creator_name || comic.artist_name || 'C')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{comic.creator_name || comic.artist_name}</p>
                      <p className="text-xs text-[var(--text-secondary)]">View Profile</p>
                    </div>
                    <ChevronRight size={16} className="text-[var(--text-secondary)]" />
                  </Link>
                </div>
              </div>
              
              {/* Sidebar Ad */}
              <AdBanner size="rectangle" variant="default" className="rounded-xl" comicId={comicId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComicDetailPage;
